import { NgStyle } from '@angular/common';
import { Component, ElementRef, EventEmitter, Output, afterNextRender, effect, inject, signal, untracked, viewChild } from '@angular/core';

import { ResizeObserverDirective } from '@23degrees/shared/directives';
import { BreakPointManager } from '@23degrees/shared/manager/breakpoint/breakpoint.manager';
import { ChartManager } from '@23degrees/shared/manager/chart/chart.manager';
import { FormatPipe } from '@23degrees/shared/pipes';

import { LayoutElementComponent } from '../layout-element/layout-element.component';

@Component({
  selector: 'layout-legend',
  templateUrl: './layout-legend.component.html',
  styleUrls: ['./layout-legend.component.scss'],
  imports: [NgStyle, ResizeObserverDirective],
  providers: [FormatPipe],
})
export class LayoutLegendComponent extends LayoutElementComponent {
  private chartManager = inject(ChartManager, { optional: true });
  private breakPointManager = inject(BreakPointManager);

  @Output() legendEvent = new EventEmitter<any>();

  // @ViewChild('legend', { static: true }) legend;
  public legendDiv = viewChild<ElementRef<HTMLDivElement>>('legend');

  public legendStyle = signal<Partial<CSSStyleDeclaration>>({
    height: '0',
    overflowX: 'hidden',
    overflowY: 'auto',
  });

  private prevLegendWidth: number;
  private prevLegendHeight: number;

  private containerCreated = false;

  constructor() {
    super();

    afterNextRender(() => {
      const { offsetWidth, offsetHeight } = this.legendDiv().nativeElement;
      this.prevLegendWidth = offsetWidth;
      this.prevLegendHeight = offsetHeight;

      this.chartManager.createLegendContainer(this.legendDiv().nativeElement);
      this.containerCreated = true;

      // this.emitRenderStatus('legendReady');
      this.updateLegendSize(true, 'constructor');
    });

    effect(() => {
      const content = this.content();

      untracked(() => {
        this.updateLegendSize(true, 'contentSignal');
      });
    });
  }

  ngOnDestroy() {
    if (this.chartManager) {
      this.chartManager.destroyLegendContainer();
    }

    super.ngOnDestroy();
  }

  public getStyleScaled = (style: any, which: string): number => {
    let sizeFactor = 1;
    if (this.content()) {
      if (!this.content().typeSpecific.layout.oldLayoutStyle) {
        sizeFactor = this.breakPointManager.getSizeFactor(this.content().slug);
      }
    }

    let size = style[which];
    // if (style[which + 'Unit'] === 'em') size *= 16;
    // if (style[which + 'Unit'] === 'rem') size *= 16;
    if (style[`${which}Unit`] === '%') return size;
    return size * sizeFactor;
  };

  public resizeLegend = (event) => {
    this.updateLegendSize(false, 'resizeLegend');
  };

  private updateLegendSize = (force, who) => {
    if (!this.config()) return;
    if (!this.chartManager) return;
    if (!this.containerCreated) return;

    const { offsetWidth, offsetHeight, scrollWidth, scrollHeight } = this.legendDiv().nativeElement;
    // if (offsetWidth === 0 || offsetHeight === 0) return;
    if (Number.isNaN(offsetWidth) || Number.isNaN(offsetHeight)) return;

    const { content, data, range } = this.config();
    if (!content || !data || !range) return;

    if (data && data.length === 0) {
      this.emitRenderStatus('legendReady');
      return;
    }

    // console.log('updateLegendSize 1', who, offsetWidth, offsetHeight);

    if (force || offsetWidth !== this.prevLegendWidth || offsetHeight !== this.prevLegendHeight) {
      this.prevLegendWidth = offsetWidth;
      this.prevLegendHeight = offsetHeight;

      // console.log('updateLegendSize 2', who, offsetWidth, offsetHeight);

      if (this.content().typeSpecific.chart.legend.enabled && offsetWidth > 0) {
        this.chartManager.updateContentData(content, data, range, offsetWidth);

        const { style, scrollable, scrollHeight } = this.content().typeSpecific.chart.legend;
        const height = this.chartManager.updateLegendContainerSize(offsetWidth, offsetHeight);
        const marginTop = this.getStyleScaled(style.margin, 'top') + style.margin.topUnit;
        const marginBottom = this.getStyleScaled(style.margin, 'bottom') + style.margin.bottomUnit;

        this.legendStyle.update((value) => ({
          ...value,
          height: `${height}px`,
          maxHeight: scrollable ? `${scrollHeight}px` : undefined,
          marginTop: marginTop,
          marginBottom: marginBottom,
        }));

        this.chartManager.updateLegend();

        if (height > 0) {
          this.emitRenderStatus('legendReady');
        }
      } else {
        this.legendStyle.update((value) => ({
          ...value,
          height: '0',
          maxHeight: '0',
          marginTop: '0',
          marginBottom: '0',
        }));

        this.chartManager.updateLegend();

        this.emitRenderStatus('legendReady');
      }
    }
  };
}
